import React from 'react';
import {AgGridReact} from 'ag-grid-react';
import type {AgGridReactProps} from 'ag-grid-react';
import {gridTheme} from './gridTheme';
import './agGrid.scss';

// long enough that the tooltip stays out of the way while the eye travels over the rows
const TOOLTIP_SHOW_DELAY = 300;

// as tight as a heading reads at Gaia's body size
const HEADER_HEIGHT = 40;

export interface AppGridProps<TRow> extends AgGridReactProps<TRow> {
    /**
     * Extra class on the scrolling container, for the page's own cell rules. The container
     * always carries `app-grid` as well, which is what the shared stylesheet is written against.
     */
    className?: string;
    dataCy?: string;
    /** The measured height, in pixels - see useAvailableHeight. The stylesheet's own height stands without it. */
    height?: number | null;
    /** The container element, for a page that measures it or drives focus inside it. */
    containerRef?: (element: HTMLDivElement | null) => void;
    onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
}

/**
 * An ag-grid wearing this app's Gaia theme, in the scrolling container the shared stylesheet
 * expects (see agGrid.scss).
 *
 * Everything below is a default: a page passing the same prop overrides it, since its props
 * are spread after these.
 */
export const AppGrid = <TRow,>({
                                   className,
                                   dataCy,
                                   height,
                                   containerRef,
                                   onKeyDown,
                                   ...gridProps
                               }: AppGridProps<TRow>) => (
    <div className={className ? `app-grid ${className}` : 'app-grid'}
         data-cy={dataCy}
         ref={containerRef}
         style={height ? {height} : undefined}
         onKeyDown={onKeyDown}>
        <AgGridReact<TRow>
            theme={gridTheme}
            headerHeight={HEADER_HEIGHT}
            defaultColDef={{resizable: true}}
            // the rows are the tab stops, and the cells are read as plain text
            suppressCellFocus={true}
            // the tab order walks the rows in display order, so the DOM has to be in that
            // order too - for the reader as much as for the fallback the browser provides
            ensureDomOrder={true}
            tooltipShowDelay={TOOLTIP_SHOW_DELAY}
            // the values are readable in the cell; only a clipped one needs spelling out
            tooltipShowMode="whenTruncated"
            domLayout="normal"
            {...gridProps}
        />
    </div>
);

export default AppGrid;
